import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Building2, ExternalLink, Minus, Plus, StickyNote, Trash2 } from 'lucide-react';
import { ORDER_LINE_QUANTITY_MAX, type OrderFulfilment } from '@his/shared';
import { money } from '@/lib/utils';
import { Badge, Button, Input } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { removeFromDraft, setFulfilment, setLineNotes, setQuantity } from './draft';
import type { DraftLine, OrderKind } from './types';

function QuantityStepper({ line, onChange }: { line: DraftLine; onChange: (quantity: number) => void }) {
  return (
    <div className="inline-flex shrink-0 items-center rounded-lg border bg-background" role="group" aria-label={tr('opd', 'order_quantity')}>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        aria-label={tr('opd', 'order_decrease_x', { name: line.name })}
        disabled={line.quantity <= 1}
        onClick={() => onChange(line.quantity - 1)}
      >
        <Minus className="h-3.5 w-3.5" aria-hidden />
      </Button>
      <span className="min-w-[2ch] px-1 text-center text-sm font-semibold tabular" aria-live="polite">
        {line.quantity}
      </span>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        aria-label={tr('opd', 'order_increase_x', { name: line.name })}
        disabled={line.quantity >= ORDER_LINE_QUANTITY_MAX}
        onClick={() => onChange(line.quantity + 1)}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </div>
  );
}

function AdmissionLineState({ line }: { line: DraftLine }) {
  if (line.inHouse) return null;
  return (
    <div className="mt-2">
      <Badge variant="destructive">{tr('opd', 'io_ord_cannot_here')}</Badge>
    </div>
  );
}

function DraftRow({
  kind,
  line,
  lines,
  admission,
  onChange,
}: {
  kind: OrderKind;
  line: DraftLine;
  lines: readonly DraftLine[];
  admission: boolean;
  onChange: (next: DraftLine[]) => void;
}) {
  const hint = line.hint ? (kind === 'radiology' ? line.hint.toUpperCase() : line.hint) : null;
  return (
    <li className="rounded-xl border bg-card p-3 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{line.name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span className="font-mono">{line.code}</span>
            {hint ? ` · ${hint}` : ''}
            {line.priced ? ` · ${money(line.priceMinor)}` : ''}
          </p>
        </div>
        <QuantityStepper line={line} onChange={(quantity) => onChange(setQuantity(lines, line.chargeItemId, quantity))} />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
          aria-label={tr('opd', 'order_remove_x', { name: line.name })}
          onClick={() => onChange(removeFromDraft(lines, line.chargeItemId))}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      {admission ? <AdmissionLineState line={line} /> : <FulfilmentAndNote line={line} lines={lines} onChange={onChange} />}
    </li>
  );
}

function FulfilmentAndNote({ line, lines, onChange }: { line: DraftLine; lines: readonly DraftLine[]; onChange: (next: DraftLine[]) => void }) {
  const [noting, setNoting] = useState(line.notes.length > 0);
  return (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {line.inHouse ? (
          <div className="w-full sm:w-64">
            <ChoiceButtons<OrderFulfilment>
              label={tr('opd', 'order_who_does_it')}
              value={line.fulfilment}
              onChange={(value) => onChange(setFulfilment(lines, line.chargeItemId, value))}
              options={[
                { value: 'in_house', label: tr('opd', 'order_done_here'), icon: Building2 },
                { value: 'outside', label: tr('opd', 'order_outside'), icon: ExternalLink, tone: 'warning' },
              ]}
            />
          </div>
        ) : (
          <span className="inline-flex items-center gap-2">
            <Badge variant="warning">{tr('opd', 'order_outside_only')}</Badge>
            <span className="text-xs text-muted-foreground">{tr('opd', 'order_cannot_be_done_here')}</span>
          </span>
        )}
        {!noting && (
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setNoting(true)}>
            <StickyNote className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'order_add_note')}
          </Button>
        )}
      </div>
      {noting && (
        <Input
          className="mt-2"
          value={line.notes}
          maxLength={600}
          placeholder={tr('opd', 'order_line_note')}
          aria-label={tr('opd', 'order_line_note_for_x', { name: line.name })}
          onChange={(event) => onChange(setLineNotes(lines, line.chargeItemId, event.target.value))}
        />
      )}
    </>
  );
}

export function DraftOrderList({
  kind,
  lines,
  admission = false,
  onChange,
}: {
  kind: OrderKind;
  lines: readonly DraftLine[];
  admission?: boolean;
  onChange: (next: DraftLine[]) => void;
}) {
  if (lines.length === 0) {
    return <div className="grid min-h-40 place-items-center rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">{tr('opd', 'order_draft_empty')}</div>;
  }
  return (
    <ul className="space-y-2">
      {lines.map((line) => (
        <DraftRow key={line.chargeItemId} kind={kind} line={line} lines={lines} admission={admission} onChange={onChange} />
      ))}
    </ul>
  );
}
